// frontend/src/ModerationIndicator.tsx - Moderation Status Indicator Component

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';

interface ModerationIndicatorProps {
  processId: number;
  size?: 'small' | 'medium' | 'large';
  showText?: boolean;
  className?: string;
}

interface ModerationStatus {
  recommended: boolean;
  urgency: 'none' | 'low' | 'medium' | 'high' | 'error' | 'unknown';
  message: string;
}

const ModerationIndicator: React.FC<ModerationIndicatorProps> = ({
  processId,
  size = 'medium',
  showText = true,
  className = ''
}) => {
  const [status, setStatus] = useState<ModerationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchModerationStatus = async () => {
      try {
        const token = Cookies.get('access_token');
        const response = await axios.get(
          `${API_URL}/api/moderation/process/${processId}/recommendation`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setStatus(response.data);
      } catch (error) {
        // Silently handle errors - show no indicator if analysis unavailable
        setStatus({
          recommended: false,
          urgency: 'none',
          message: 'Analysis unavailable'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (processId > 0) {
      fetchModerationStatus();
    }
  }, [processId]);

  if (isLoading) {
    return (
      <div className={`moderation-indicator loading ${size} ${className}`}>
        <div style={{ 
          width: size === 'small' ? '12px' : size === 'medium' ? '16px' : '20px',
          height: size === 'small' ? '12px' : size === 'medium' ? '16px' : '20px',
          borderRadius: '50%',
          background: '#e5e7eb',
          animation: 'pulse 1.5s ease-in-out infinite'
        }} />
      </div>
    );
  }

  if (!status || !status.recommended) {
    return null; // Don't show indicator if moderation not needed
  }

  // Determine colors and icons based on urgency
  const getIndicatorStyle = (urgency: string) => {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: size === 'small' ? '0.25rem 0.5rem' : size === 'medium' ? '0.375rem 0.75rem' : '0.5rem 1rem',
      borderRadius: size === 'small' ? '4px' : '6px',
      fontSize: size === 'small' ? '0.75rem' : size === 'medium' ? '0.875rem' : '1rem',
      fontWeight: '600',
      border: '1px solid',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    };

    switch (urgency) {
      case 'high':
        return {
          ...baseStyle,
          backgroundColor: '#fef2f2',
          color: '#dc2626',
          borderColor: '#fecaca'
        };
      case 'medium':
        return {
          ...baseStyle,
          backgroundColor: '#fefbf2',
          color: '#d97706',
          borderColor: '#fed7aa'
        };
      case 'low':
        return {
          ...baseStyle,
          backgroundColor: '#f0f9ff',
          color: '#2563eb',
          borderColor: '#bfdbfe'
        };
      default:
        return {
          ...baseStyle,
          backgroundColor: '#f9fafb',
          color: '#6b7280',
          borderColor: '#d1d5db'
        };
    }
  };

  const getIcon = (urgency: string) => {
    switch (urgency) {
      case 'high':
        return '⚠️';
      case 'medium':
        return '📊';
      case 'low':
        return 'ℹ️';
      default:
        return '📋';
    }
  };

  const handleClick = () => {
    // Future: Open moderation analysis modal or navigate to moderation page
    console.log(`Moderation needed for process ${processId}:`, status);
    alert(`Moderation Analysis:\n\n${status.message}\n\nFuture: This will open the detailed variance analysis.`);
  };

  return (
    <div 
      style={getIndicatorStyle(status.urgency)}
      className={`moderation-indicator ${className}`}
      onClick={handleClick}
      title={`Click for detailed variance analysis - ${status.message}`}
    >
      <span>{getIcon(status.urgency)}</span>
      {showText && (
        <span>
          {status.urgency === 'high' ? 'High Variance' :
           status.urgency === 'medium' ? 'Moderate Variance' :
           status.urgency === 'low' ? 'Low Variance' :
           'Analysis'}
        </span>
      )}
    </div>
  );
};

export default ModerationIndicator;